import React, {useEffect, useState} from 'react'
import {Button} from "primereact/button";
import FetchUtil from "../../../../util/FetchUtil";
import ConfigUtil from "../../../../util/ConfigUtil";
import ErrorInfoComponent from "../../../general/ErrorInfoComponent";

const getPlayerLabel = (player) => player.lastname + ", " + player.firstname;

/**
 * Auswahl der Gastspieler aus BDV/nu bei der Gasteingabe im neuen Design. Entspricht für den öffentlichen Bereich
 * (Aufruf mit QR-Code) dem NuPlayerAuswahlComponent, das in der alten Ansicht unverändert verwendet wird.
 */
export default function GasteingabeNuSpielerauswahl({tournamentId, qrCode, externMeetingId, tournamentPlayers, onClose, onRefresh}) {

    const [infoMessage, setInfoMessage] = useState(undefined);
    const [errorMessage, setErrorMessage] = useState(undefined);

    const [nuPlayers, setNuPlayers] = useState(undefined);
    const [filter, setFilter] = useState("");
    // Spieler, dessen Übernahme gerade bestätigt werden soll
    const [selectedPlayer, setSelectedPlayer] = useState(undefined);
    const [working, setWorking] = useState(false);

    useEffect(() => {
        FetchUtil.fetchGet(ConfigUtil.getConfig().resourceUrls.public + "/meeting/guestplayers/" + tournamentId + "/" + qrCode,
            json => {
                setNuPlayers([...json].sort((a, b) => a.lastname.localeCompare(b.lastname)));
            },
            responseNotOk => {
                setErrorMessage("Fehler beim Laden der Spieler zur Gastmannschaft: " + responseNotOk.message);
                setNuPlayers([]);
            },
            error => {
                setErrorMessage("Fehler beim Laden der Spieler zur Gastmannschaft: " + error.message);
                setNuPlayers([]);
            });
    }, []);

    const savePlayer = () => {
        setWorking(true);
        setErrorMessage(undefined);
        const data = {externMeetingId: externMeetingId, tournamentId: tournamentId, heim: false, gast: true,
            externPlayerId: selectedPlayer.nuLigaPlayerId,
            externPersonId: selectedPlayer.nuLigaPersonId,
            lastname: selectedPlayer.lastname,
            firstname: selectedPlayer.firstname,
        };
        FetchUtil.fetchPost(ConfigUtil.getConfig().resourceUrls.public + "/nu/saveplayer/" + qrCode,
            data,
            () => {
                setInfoMessage(getPlayerLabel(selectedPlayer) + " wurde übernommen.");
                onRefresh();
                setSelectedPlayer(undefined);
                setWorking(false);
            },
            responseNotOk => {
                setErrorMessage("Fehler: " + responseNotOk.message);
                setSelectedPlayer(undefined);
                setWorking(false);
            },
            error => {
                setErrorMessage("Fehler: " + error.message);
                setSelectedPlayer(undefined);
                setWorking(false);
            }
        );
    }

    const isUebernommen = (player) => tournamentPlayers.some(p => p.externPlayerId === player.nuLigaPlayerId);

    const passtZumFilter = (player) => {
        const text = filter.trim().toUpperCase();
        return text === "" || (getPlayerLabel(player) + " " + player.nuLigaPersonId).toUpperCase().includes(text);
    }

    const getSpielerKnopf = (player) => {
        const uebernommen = isUebernommen(player);
        return <button key={player.nuLigaPlayerId} type="button" className="dhpt-gast-spieler dhpt-gast-nu-spieler"
                       onClick={() => setSelectedPlayer(player)}>
            <span className="dhpt-gast-name">{getPlayerLabel(player)}</span>
            <span className="dhpt-gast-ebene-zusatz">{player.nuLigaPersonId}</span>
            {uebernommen && <span className="dhpt-gast-uebernommen"><i className="pi pi-check" aria-hidden="true"/>übernommen</span>}
        </button>
    }

    const getListe = () => {
        if (nuPlayers === undefined) {
            return <p className="dhpt-hinweis"><i className="pi pi-spin pi-spinner" aria-hidden="true"/> Spieler werden von nu geladen …</p>
        }
        if (nuPlayers.length === 0) {
            return <p className="dhpt-hinweis">Keine Spielerdaten von nu vorhanden.</p>
        }
        // Spieler mit negativer Personennummer sind in nu ohne Person angelegt, die stehen wie bisher am Ende
        const mitPerson = nuPlayers.filter(player => player.nuLigaPersonId >= 0 && passtZumFilter(player));
        const ohnePerson = nuPlayers.filter(player => player.nuLigaPersonId < 0 && passtZumFilter(player));
        if (mitPerson.length === 0 && ohnePerson.length === 0) {
            return <p className="dhpt-hinweis">Kein Spieler zu deiner Suche gefunden.</p>
        }
        return <div className="dhpt-gast-spielerliste">
            {mitPerson.map(player => getSpielerKnopf(player))}
            {ohnePerson.length > 0 && <>
                <div className="dhpt-gast-phasen-label dhpt-gast-trenner">Weitere Spieler</div>
                {ohnePerson.map(player => getSpielerKnopf(player))}
            </>}
        </div>
    }

    const getBestaetigung = () => {
        return <>
            <p>Soll <b>{getPlayerLabel(selectedPlayer)}</b> ({selectedPlayer.nuLigaPersonId}) als Spieler übernommen werden?</p>
            <p className="dhpt-hinweis">Ist der Spieler bereits vorhanden, werden seine Daten in Darthelfer aktualisiert.</p>
            <div className="dhpt-gast-ebene-knoepfe">
                <Button icon={working ? "pi pi-spin pi-spinner" : "pi pi-check"} label="Übernehmen" disabled={working} onClick={() => savePlayer()}/>
                <Button icon="pi pi-arrow-left" label="Zurück" className="p-button-outlined dhpt-button-hell" disabled={working}
                        onClick={() => setSelectedPlayer(undefined)}/>
            </div>
        </>
    }

    return <div className="dhpt-gast-ebene" role="dialog" aria-modal="true" aria-label="Spieler aus nu auswählen">
        <ErrorInfoComponent infoMessage={infoMessage} errorMessage={errorMessage}
                            onClearInfoMessage={() => setInfoMessage(undefined)}
                            onClearErrorMessage={() => setErrorMessage(undefined)}/>
        <div className="dhpt-gast-ebene-fenster dhpt-gast-ebene-gross">
            <div className="dhpt-gast-ebene-kopf">
                <div>
                    <div className="dhpt-fett">{selectedPlayer ? "Spieler übernehmen" : "Spieler aus nu auswählen"}</div>
                    <div className="dhpt-gast-ebene-zusatz">Gastmannschaft</div>
                </div>
                <button type="button" className="dhpt-gast-knopf" aria-label="Schließen" onClick={() => onClose()}>
                    <i className="pi pi-times" aria-hidden="true"/>
                </button>
            </div>
            {selectedPlayer
                ? getBestaetigung()
                : <>
                    <div className="dhpt-suche dhpt-gast-nu-suche">
                        <i className="pi pi-search dhpt-suche-icon" aria-hidden="true"/>
                        <input type="search" className="dhpt-suche-feld" placeholder="Name oder Personennummer …"
                               aria-label="Spieler suchen" value={filter} onChange={(e) => setFilter(e.target.value)}/>
                    </div>
                    {getListe()}
                </>
            }
        </div>
    </div>
}
